import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { useDeleteProduct, useProduct } from "@/api/products-query";
import { AlertDialog } from "@/components/dashboard/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/store/$productid")({
	component: RouteComponent
});

function RouteComponent() {
	const { productid } = Route.useParams();
	const { data: product, isPending, isError } = useProduct(productid);

	const deleteProduct = useDeleteProduct();

	return (
		<section className="flex flex-col space-y-4 px-2 py-6">
			<div className="flex items-center justify-end gap-2 mb-2">
				<Button asChild>
					<Link to="/vendor/contract/create">
						<Plus className="h-4 w-6" />
						Generate Contract
					</Link>
				</Button>
			</div>

			<div className={cn("min-h-[calc(100vh-12rem)] bg-gray-50 rounded-xl p-4 md:p-6 mx-auto min-w-[80vw] ", {
				"flex items-center justify-center": !product || isPending || isError
			})}
			>
				{isPending
					? (
							<div className="flex flex-col items-center justify-center gap-4">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
								<p className="text-muted-foreground">Loading product details...</p>
							</div>
						)
					: isError
						? (
								<div className="text-center space-y-4">
									<div className="text-destructive text-5xl mb-4">⚠️</div>
									<h2 className="text-xl font-semibold text-destructive">Error fetching data</h2>
									<p className="text-muted-foreground">Please try again later</p>
								</div>
							)
						: product
							? (
									<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
										<ProductCarousel images={product.photos} />
										<div className="flex flex-col h-full ">
											<h1 className="text-2xl font-medium capitalize mb-2">{product.name}</h1>
											<h3 className="text-2xl font-semibold mb-6">
												{formatCurrency(parseInt(product.price) ?? 0, "NGN")}
											</h3>
											<div className="flex-grow my-12">
												<h2 className="text-lg font-medium mb-2">Description</h2>
												<p className="text-muted-foreground">{product.description}</p>
											</div>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">

												<Button size="lg" className="h-14">
													<Link
														to="/vendor/store/edit/$productid"
														params={{ productid: product.id }}
													>
														Edit this product
													</Link>
												</Button>

												<AlertDialog
													description="This action cannot be undone. This will permanently delete your this product."
													onClick={() => deleteProduct.mutate(product.id)}
												>
													<Button
														variant="destructive"
														size="lg"
														className="h-14 bg-[#FFECEC] hover:bg-[#ffe8e8] text-destructive"
													>
														<Trash2 className="h-4 w-4 mr-2" />
														Delete this product
													</Button>
												</AlertDialog>

											</div>
										</div>
									</div>
								)
							: (
									<div className="text-center space-y-4">
										<div className="mx-auto flex items-center justify-center bg-secondary w-48 h-48 rounded-full">
											<img src="/no-products.png" alt="No product available" className="w-32 h-32 opacity-75" />
										</div>
										<h2 className="text-xl font-bold">Product unavailable</h2>
										<p className="text-muted-foreground">This product might have been removed or doesn't exist</p>
									</div>
								)}
			</div>
		</section>
	);
}

function ProductCarousel({ images }: { className?: string; images: string[] }) {
	const [currentSlide, setCurrentSlide] = useState(0);

	const goToSlide = (index: number) => {
		setCurrentSlide(index);
	};

	const goToPrevSlide = () => {
		setCurrentSlide(prevSlide => (prevSlide - 1 + images.length) % images.length);
	};

	const goToNextSlide = () => {
		setCurrentSlide(prevSlide => (prevSlide + 1) % images.length);
	};

	return (
		<div className="w-full">
			<div className="relative aspect-square md:aspect-[4/3] w-full overflow-hidden rounded-lg bg-white">
				<div
					className="absolute top-0 left-0 w-full h-full flex transition-transform duration-300 ease-in-out"
					style={{ transform: `translateX(-${currentSlide * 100}%)` }}
				>
					{
						images.length > 0
							? images.map((image, index) => (
									<div
										key={`slide-${crypto.randomUUID()}`}
										className="w-full h-full flex-shrink-0 relative bg-gray-100"
									>
										<img
											src={image ?? "/placeholder.svg"}
											alt={`Product image ${index + 1}`}
											className="absolute inset-0 w-full h-full object-contain p-4"
											loading={index === 0 ? "eager" : "lazy"}
										/>
									</div>
								))

							: (
									<div
										className="w-full h-full flex-shrink-0 relative bg-gray-100"
									>
										<img
											src="/placeholder.svg"
											alt="Image placeholder"
											className="absolute inset-0 w-full h-full object-contain p-4"
										/>
									</div>
								)

					}
				</div>

				{images.length > 1 && (
					<>
						<Button
							variant="secondary"
							onClick={goToPrevSlide}
							className="absolute top-1/2 left-2 -translate-y-1/2 w-10 h-10 rounded-full shadow-lg opacity-90 hover:opacity-100"
							aria-label="Previous image"
						>
							<ChevronLeft className="!size-6" />
						</Button>

						<Button
							variant="secondary"
							onClick={goToNextSlide}
							className="absolute top-1/2 right-2 -translate-y-1/2 w-10 h-10 rounded-full shadow-lg opacity-90 hover:opacity-100"
							aria-label="Next image"
						>
							<ChevronRight className="!size-6" />
						</Button>
					</>
				)}
			</div>
			<div className="mt-4 flex justify-center space-x-2">
				{images.map((_, index) => (
					<Button
						variant="secondary"
						key={crypto.randomUUID()}
						onClick={() => goToSlide(index)}
						className={cn("w-16 h-16 rounded-md overflow-hidden", {
							"ring-2 ring-primary": currentSlide === index
						})}
						aria-label={`Go to image ${index + 1}`}
					>
						<div className="relative w-full h-full">
							<img
								src={images[index]}
								alt={`Thumbnail ${index + 1}`}
								className="w-full h-full object-cover"
							/>
						</div>
					</Button>
				))}
			</div>
		</div>
	);
}
